import { useCallback } from "react"

import type { Attachment } from "./useAttachments"
import {
	ALLOWED_FILE_SIZE_IN_MB,
	ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB,
} from "../constants"

const MAX_FILE_SIZE_BYTES = ALLOWED_FILE_SIZE_IN_MB * 1024 * 1024
const MAX_FILES_POSTMARK_ATTACHMENTS_SIZE_IN_MB =
	ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB * 1024 * 1024

export const useAttachmentsValidation = ({
	exisitingAttachments = [],
	errorHandler,
}: {
	exisitingAttachments: Attachment[]
	errorHandler: (options: { title: string; description: string }) => void
}) => {
	// Validation functions with error handling
	const validateFileSize = useCallback(
		(attachments: Attachment[]): Attachment[] =>
			attachments.filter((attachment) => {
				if (attachment.size > MAX_FILE_SIZE_BYTES) {
					errorHandler({
						title: `Invalid file size`,
						description: `${attachment.name} exceeds the ${ALLOWED_FILE_SIZE_IN_MB} MB limit.`,
					})
					return false
				}

				return true
			}),
		[errorHandler],
	)

	const validateDuplicateFile = useCallback(
		(newAttachments: Attachment[]): Attachment[] => {
			const fresh = newAttachments.filter((attachment) => {
				const isSameFile = exisitingAttachments.some((existing) =>
					isSameAttachmentFile(existing, attachment),
				)
				if (isSameFile) {
					errorHandler({
						title: `Duplicate file`,
						description: `${attachment.name} is already added.`,
					})
				}
				return !isSameFile
			})
			return withUniqueNames(fresh, exisitingAttachments)
		},
		[errorHandler, exisitingAttachments],
	)

	const validateTotalImageSize = useCallback(
		(attachments: Attachment[]): Attachment[] => {
			const totalImageSize = [...attachments, ...exisitingAttachments]
				.filter((attachment) => attachment.type.startsWith("image/"))
				.reduce((acc, attachment) => acc + attachment.size, 0)

			if (totalImageSize > MAX_FILES_POSTMARK_ATTACHMENTS_SIZE_IN_MB) {
				errorHandler({
					title: `Image size limit exceeded`,
					description: `Total image size exceeds the ${ALLOWED_POSTMARK_ATTACHMENTS_SIZE_IN_MB} MB limit.`,
				})
				return attachments.filter(
					(attachment) => !attachment.type.startsWith("image/"),
				)
			}

			return attachments
		},
		[errorHandler, exisitingAttachments],
	)

	const validateAttachments = useCallback(
		(newAttachments: Attachment[]): Attachment[] =>
			[newAttachments]
				.map(validateFileSize)
				.map(validateDuplicateFile)
				.map(validateTotalImageSize)
				.pop() || [],
		[validateFileSize, validateDuplicateFile, validateTotalImageSize],
	)

	return { validateAttachments }
}

// The same file picked twice, not two files that happen to share a name.
function isSameAttachmentFile(existing: Attachment, added: Attachment) {
	return (
		existing.id !== added.id &&
		existing.name === added.name &&
		existing.size === added.size &&
		existing.file?.lastModified === added.file?.lastModified
	)
}

/** Renames clashing attachments ("image.png" → "image-2.png"), since every pasted screenshot is called image.png. */
export function withUniqueNames(
	added: Attachment[],
	existing: Attachment[],
): Attachment[] {
	const taken = new Set(existing.map((attachment) => attachment.name))
	return added.map((attachment) => {
		const name = uniqueName(attachment.name, taken)
		taken.add(name)
		return name === attachment.name ? attachment : { ...attachment, name }
	})
}

function uniqueName(name: string, taken: Set<string>): string {
	if (!taken.has(name)) return name
	const dot = name.lastIndexOf(".")
	const base = dot > 0 ? name.slice(0, dot) : name
	const extension = dot > 0 ? name.slice(dot) : ""
	let counter = 2
	while (taken.has(`${base}-${counter}${extension}`)) counter++
	return `${base}-${counter}${extension}`
}
